import type { CipherSuite } from "@hpke/core";
import { createHpkeSuite } from "../../constants";
import { decodeBytes, encodeBytes } from "../../primitives";
import type { EncryptablePayload, EncryptionResult } from "../../types";

export interface HPKEEncryptParameters<T extends Record<string, unknown>> {
	data: T;
	recipientPublicKey: CryptoKey;
	senderKeyPair: CryptoKeyPair;
	authMode: boolean;
}

export interface HPKEDecryptParameters<U extends string | ArrayBuffer> {
	ciphertext: U;
	encapsulatedKey: U;
	recipientKeyPair: CryptoKeyPair;
	senderPublicKey?: CryptoKey;
}

export class HPKE {
	constructor(public readonly suite: CipherSuite = createHpkeSuite()) {}

	private async encrypt<T extends EncryptablePayload>(
		data: T,
		recipientPublicKey: CryptoKey,
		senderKeyPair: CryptoKeyPair,
		authMode: boolean,
	): Promise<Omit<EncryptionResult<ArrayBuffer>, "publicKey">> {
		const serializedPublicKey = await this.suite.kem.serializePublicKey(
			senderKeyPair.publicKey,
		);
		return this.encryptRaw(
			this.serialize({
				data,
				encryptionContext: {
					senderPublicKey: this.bufferToBase64(serializedPublicKey),
				},
			}),
			recipientPublicKey,
			authMode ? senderKeyPair : undefined,
		);
	}

	private async encryptRaw(
		data: ArrayBuffer,
		recipientPublicKey: CryptoKey,
		senderKey?: CryptoKeyPair,
	): Promise<Omit<EncryptionResult<ArrayBuffer>, "publicKey">> {
		try {
			const senderContext = await this.suite.createSenderContext({
				recipientPublicKey,
				senderKey,
			});
			const ciphertext = await senderContext.seal(data);

			return {
				ciphertext,
				encapsulatedKey: senderContext.enc,
			};
		} catch (error) {
			console.error(`[HPKE] Encryption failed: ${error}`);
			throw new Error("Failed to encrypt data");
		}
	}

	public async encryptBase64<T extends Record<string, unknown>>(
		options: HPKEEncryptParameters<T>,
	): Promise<EncryptionResult<string>> {
		const { data, recipientPublicKey, senderKeyPair, authMode } = options;
		const { ciphertext, encapsulatedKey } = await this.encrypt(
			data,
			recipientPublicKey,
			senderKeyPair,
			authMode,
		);
		const publicKey = await this.suite.kem.serializePublicKey(
			senderKeyPair.publicKey,
		);

		return {
			ciphertext: this.bufferToBase64(ciphertext),
			encapsulatedKey: this.bufferToBase64(encapsulatedKey),
			publicKey: this.bufferToBase64(publicKey),
		};
	}

	public async decrypt<
		T extends EncryptablePayload,
		U extends string | ArrayBuffer,
	>(options: HPKEDecryptParameters<U>): Promise<T> {
		const { ciphertext, encapsulatedKey, recipientKeyPair, senderPublicKey } =
			options;
		try {
			const recipient = await this.suite.createRecipientContext({
				recipientKey: recipientKeyPair,
				enc: this.bufferOrStringToBuffer(encapsulatedKey),
				senderPublicKey,
			});

			const plaintext = await recipient.open(
				this.bufferOrStringToBuffer(ciphertext),
			);
			return this.deserialize<T>(plaintext);
		} catch (error) {
			console.error(`[HPKE] Decryption failed: ${error}`);
			throw new Error("Failed to decrypt data");
		}
	}

	private serialize<T extends EncryptablePayload>(data: T): ArrayBuffer {
		const uint8Array = new TextEncoder().encode(JSON.stringify(data));
		const arrayBuffer = new ArrayBuffer(uint8Array.length);
		new Uint8Array(arrayBuffer).set(uint8Array);
		return arrayBuffer;
	}

	private deserialize<T extends EncryptablePayload>(data: ArrayBuffer): T {
		return JSON.parse(new TextDecoder().decode(data)) as T;
	}

	private bufferToBase64(buffer: ArrayBuffer): string {
		return encodeBytes(new Uint8Array(buffer), "base64");
	}

	private base64ToBuffer(base64: string): ArrayBuffer {
		const uint8Array = decodeBytes(base64, "base64");
		const arrayBuffer = new ArrayBuffer(uint8Array.length);
		new Uint8Array(arrayBuffer).set(uint8Array);
		return arrayBuffer;
	}

	private bufferOrStringToBuffer(value: string | ArrayBuffer): ArrayBuffer {
		return typeof value === "string" ? this.base64ToBuffer(value) : value;
	}
}
